import { ColumnDef } from "@tanstack/react-table";
import { VoucherResponse } from "@/app/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Edit, Trash2, Ban, Eye, Calendar, FileText, User, DollarSign, Tag } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { format } from 'date-fns';

// Define custom meta type
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

export const columnsVoucher: ExtendedColumnDef<VoucherResponse>[] = [
  {
    accessorKey: "transaction_date",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => format(new Date(row.original.transaction_date), 'dd/MM/yyyy'),
    meta: {
      label: 'Date',
      placeholder: 'Select date range...',
      variant: 'dateRange',
      icon: Calendar,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: "voucher_number",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Voucher #" />,
    cell: ({ row }) => <span className="font-mono">{row.original.voucher_number}</span>,
    meta: {
      label: 'Voucher Number',
      placeholder: 'Filter vouchers...',
      variant: 'text',
      icon: FileText,
    },
    enableColumnFilter: true,
  },
  {
    id: "voucher_type_id",
    accessorKey: "voucher_type_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }) => row.original.voucher_type?.name ?? '-',
    meta: {
      label: 'Voucher Type',
      placeholder: 'Filter types...',
      variant: 'multiSelect',
      icon: Tag,
      options: [], // Will be populated dynamically
    },
    enableColumnFilter: true,
  },
  {
    id: "party.name",
    accessorKey: "party.name", // Access nested data
    header: ({ column }) => <DataTableColumnHeader column={column} title="Party" />,
    cell: ({ row }) => row.original.party?.name ?? '-',
    meta: {
      label: 'Party',
      placeholder: 'Filter parties...',
      variant: 'text',
      icon: User,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: "total_amount",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => row.original.total_amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }), // Format as currency
    meta: {
      label: 'Amount',
      placeholder: 'Filter by amount...',
      variant: 'number',
      icon: DollarSign,
    },
    enableColumnFilter: true,
  },
  {
    id: "status",
    accessorKey: "is_cancelled",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const isCancelled = row.original.is_cancelled;
      return (
        <Badge variant={isCancelled ? "destructive" : "default"}>
          {isCancelled ? "Cancelled" : "Active"}
        </Badge>
      );
    },
    filterFn: (row, id, value) => value.includes(String(row.getValue(id))),
    meta: {
      label: 'Status',
      placeholder: 'Filter by status...',
      variant: 'select',
      icon: Tag,
      options: [
        { label: 'Active', value: 'false' },
        { label: 'Cancelled', value: 'true' },
      ],
    },
    enableColumnFilter: true,
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const voucher = row.original;
      const dispatchEvent = (eventName: string, detail: VoucherResponse): void => {
        document.dispatchEvent(new CustomEvent(eventName, { detail }));
      };
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="h-8 w-8 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!voucher.is_cancelled ? (
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                dispatchEvent("cancel-voucher", voucher);
              }} className="text-orange-600">
                <Ban className="mr-2 h-4 w-4" /> Cancel Voucher
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled className="text-muted-foreground">
                <Ban className="mr-2 h-4 w-4" /> Already Cancelled
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 